import { createElement, useEffect, useState, type ReactElement } from 'react'
import { api } from '../lib/api'
import { useMe } from '../lib/me'
import { OnboardingSurvey } from '../components/OnboardingSurvey'
import type { OnboardingAnswers } from '../../shared/onboarding'

/**
 * The onboarding questionnaire, asked once per account on whichever page
 * the person lands on first: sign-up opens their first canvas, a return
 * visit opens Home, so both mount this. Returns the modal to render while
 * the account has no answers on record, null otherwise. A "view as" session
 * is read-only and the admin is not the person to ask.
 */
export function useOnboardingAsk(userId: string | undefined): ReactElement | null {
  const me = useMe(userId)
  const [state, setState] = useState<'unknown' | 'asking' | 'done'>('unknown')
  const ask = !!me && !me.impersonating
  useEffect(() => {
    if (!ask) return
    let live = true
    api
      .onboarding()
      .then(({ profile }) => live && setState(profile ? 'done' : 'asking'))
      .catch(console.error)
    return () => {
      live = false
    }
  }, [ask])

  async function finish(answers: OnboardingAnswers) {
    await api.saveOnboarding(answers)
    setState('done')
  }

  return state === 'asking' ? createElement(OnboardingSurvey, { onDone: finish }) : null
}
